import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Trash, ArrowUp, ArrowDown, CalendarBlank, Airplane, BuildingApartment,
  MapTrifold, Car, ForkKnife, Tree, Note, Sparkle, Sun, Boat, Image as ImageIcon,
  CircleNotch, MagnifyingGlass, X, Eye, EyeSlash,
} from "@phosphor-icons/react";
import { parseDate } from "@/lib/dateUtils";
import { FlightLookupDialog } from "@/components/FlightLookupDialog";

// Add `days` whole days to a YYYY-MM-DD string, returning the same format.
// Avoids the `new Date("YYYY-MM-DD")` UTC-midnight timezone trap.
function addDaysIso(iso, days) {
  const d = parseDate(iso);
  if (!d) return "";
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const ITEM_TYPES = [
  { value: "flight", label: "Flight", icon: Airplane },
  { value: "hotel", label: "Hotel", icon: BuildingApartment },
  { value: "tour", label: "Tour", icon: MapTrifold },
  { value: "activity", label: "Activity", icon: Sparkle },
  { value: "transportation", label: "Transport", icon: Car },
  { value: "cruise", label: "Cruise", icon: Boat },
  { value: "restaurant", label: "Restaurant", icon: ForkKnife },
  { value: "free", label: "Free time", icon: Sun },
  { value: "note", label: "Note", icon: Note },
];

const ITEM_ICON_MAP = ITEM_TYPES.reduce((m, t) => ({ ...m, [t.value]: t.icon }), {});

/** Compute an item's effective sort time — falls back through type-specific
 *  fields (departure_time, check_in_time, pickup_datetime time, …) when the
 *  top-level `time` is empty. Mirrors `effectiveItemTime` in buildDayTimeline.js
 *  so the builder, live ClientView, and PDF all sort identically. */
function effectiveItemTime(item) {
  const candidates = [
    item?.time,
    item?.departure_time,
    item?.check_in_time,
    item?.check_out_time,
    item?.pickup_datetime ? String(item.pickup_datetime).match(/^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2})/)?.[1] : "",
    item?.arrival_time,
  ];
  for (const c of candidates) {
    if (!c) continue;
    const m = String(c).match(/^(\d{1,2}):(\d{2})/);
    if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  }
  return "99:99";
}

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "PHP", "JPY", "SGD", "HKD", "CHF", "NZD", "AED", "INR", "CNY", "MXN", "BRL"];

function newId() { return Math.random().toString(36).slice(2, 10); }

const inputCls = "bg-black/40 border-white/10 text-white focus:border-[#D4AF37] font-sans h-10";

function reorder(arr, from, to) {
  const next = [...arr];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function ItemRow({ item, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast, idx }) {
  const Icon = ITEM_ICON_MAP[item.type] || Note;
  const [lookupOpen, setLookupOpen] = useState(false);
  const isFlight = item.type === "flight";
  const isHotel = item.type === "hotel";
  return (
    <div className="border border-[#D4AF37]/15 bg-black/30 rounded-md p-4 mb-3" data-testid={`day-item-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center">
          <Icon size={16} weight="duotone" className="text-[#D4AF37]" />
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2.5">
          <div className="sm:col-span-3">
            <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Type</Label>
            <Select value={item.type} onValueChange={(v) => onChange({ ...item, type: v })}>
              <SelectTrigger className={inputCls} data-testid={`item-type-${idx}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-ink-surface border-white/10 text-white">
                {ITEM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Time</Label>
            <Input
              value={item.time || ""}
              onChange={(e) => onChange({ ...item, time: e.target.value })}
              placeholder="09:30"
              className={inputCls}
              data-testid={`item-time-${idx}`}
            />
          </div>
          <div className="sm:col-span-7">
            <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Title</Label>
            <Input
              value={item.title || ""}
              onChange={(e) => onChange({ ...item, title: e.target.value })}
              placeholder="e.g. Private Vatican Tour"
              className={inputCls}
              data-testid={`item-title-${idx}`}
            />
          </div>

          {/* Flight-specific structured fields — mirrors the main Flight section design */}
          {isFlight && (
            <>
              <div className="sm:col-span-12 mt-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Airplane size={12} weight="duotone" className="text-[#D4AF37]" />
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Flight details</p>
                </div>
                <Button
                  type="button"
                  onClick={() => setLookupOpen(true)}
                  variant="outline"
                  className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] bg-transparent h-8 px-3 text-[10px] uppercase tracking-widest"
                  data-testid={`item-flight-lookup-btn-${idx}`}
                >
                  <MagnifyingGlass size={12} weight="duotone" /> Lookup flight
                </Button>
              </div>
              <div className="sm:col-span-12 border border-[#D4AF37]/15 bg-black/20 rounded-md p-4 grid grid-cols-1 sm:grid-cols-12 gap-2.5">
                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Airline</Label>
                  <Input value={item.airline || ""} onChange={(e) => onChange({ ...item, airline: e.target.value })} placeholder="Emirates" className={inputCls} data-testid={`item-flight-airline-${idx}`} />
                </div>
                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Flight #</Label>
                  <Input value={item.flight_number || ""} onChange={(e) => onChange({ ...item, flight_number: e.target.value })} placeholder="EK 202" className={inputCls} data-testid={`item-flight-number-${idx}`} />
                </div>
                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Cabin / class</Label>
                  <Select value={item.cabin_class || ""} onValueChange={(v) => onChange({ ...item, cabin_class: v })}>
                    <SelectTrigger className={inputCls} data-testid={`item-flight-cabin-${idx}`}><SelectValue placeholder="Economy" /></SelectTrigger>
                    <SelectContent className="bg-ink-surface border-white/10 text-white">
                      {["Economy","Premium Economy","Business","First"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">From city</Label>
                  <Input value={item.from_city || ""} onChange={(e) => onChange({ ...item, from_city: e.target.value })} placeholder="New York" className={inputCls} data-testid={`item-flight-from-city-${idx}`} />
                </div>
                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">From airport</Label>
                  <Input value={item.from_airport || ""} onChange={(e) => onChange({ ...item, from_airport: e.target.value })} placeholder="JFK" className={inputCls} data-testid={`item-flight-from-${idx}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Dep. date</Label>
                  <Input type="date" value={item.departure_date || ""} onChange={(e) => onChange({ ...item, departure_date: e.target.value })} className={inputCls} data-testid={`item-flight-dep-date-${idx}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Dep. time</Label>
                  <Input type="time" value={item.departure_time || ""} onChange={(e) => onChange({ ...item, departure_time: e.target.value })} className={inputCls} data-testid={`item-flight-dep-time-${idx}`} />
                </div>

                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">To city</Label>
                  <Input value={item.to_city || ""} onChange={(e) => onChange({ ...item, to_city: e.target.value })} placeholder="Rome" className={inputCls} data-testid={`item-flight-to-city-${idx}`} />
                </div>
                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">To airport</Label>
                  <Input value={item.to_airport || ""} onChange={(e) => onChange({ ...item, to_airport: e.target.value })} placeholder="FCO" className={inputCls} data-testid={`item-flight-to-${idx}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Arr. date</Label>
                  <Input type="date" value={item.arrival_date || ""} onChange={(e) => onChange({ ...item, arrival_date: e.target.value })} className={inputCls} data-testid={`item-flight-arr-date-${idx}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Arr. time</Label>
                  <Input type="time" value={item.arrival_time || ""} onChange={(e) => onChange({ ...item, arrival_time: e.target.value })} className={inputCls} data-testid={`item-flight-arr-time-${idx}`} />
                </div>

                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Confirmation #</Label>
                  <Input value={item.confirmation_number || ""} onChange={(e) => onChange({ ...item, confirmation_number: e.target.value })} placeholder="ABC123" className={inputCls} data-testid={`item-flight-confirmation-${idx}`} />
                </div>
                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Supplier / contact</Label>
                  <Input value={item.supplier_contact || ""} onChange={(e) => onChange({ ...item, supplier_contact: e.target.value })} placeholder="Sabre desk · agent@email" className={inputCls} data-testid={`item-flight-supplier-${idx}`} />
                </div>
                <div className="sm:col-span-4">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Document / Attachment</Label>
                  <Input value={item.link || ""} onChange={(e) => onChange({ ...item, link: e.target.value })} placeholder="Google Drive, PDF, or booking link" className={inputCls} data-testid={`item-flight-link-${idx}`} />
                </div>

                <div className="sm:col-span-3">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Price</Label>
                  <Input value={item.price || ""} onChange={(e) => onChange({ ...item, price: e.target.value })} placeholder="1250.00" className={inputCls} data-testid={`item-flight-price-${idx}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Currency</Label>
                  <Select value={item.currency || "USD"} onValueChange={(v) => onChange({ ...item, currency: v })}>
                    <SelectTrigger className={inputCls} data-testid={`item-flight-currency-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                      {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-7">
                  <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Notes</Label>
                  <Input value={item.notes || ""} onChange={(e) => onChange({ ...item, notes: e.target.value })} placeholder="Seat preference, meal, etc." className={inputCls} data-testid={`item-flight-notes-${idx}`} />
                </div>
              </div>
            </>
          )}

          {/* Hotel-specific structured fields */}
          {item.type === "hotel" && (
            <>
              <div className="sm:col-span-12 mt-1">
                <p className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Hotel details</p>
              </div>
              <div className="sm:col-span-6">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Hotel name</Label>
                <Input value={item.hotel_name || ""} onChange={(e) => onChange({ ...item, hotel_name: e.target.value })} placeholder="Hotel de Russie" className={inputCls} data-testid={`item-hotel-name-${idx}`} />
              </div>
              <div className="sm:col-span-6">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Room type</Label>
                <Input value={item.room_type || ""} onChange={(e) => onChange({ ...item, room_type: e.target.value })} placeholder="Deluxe Suite" className={inputCls} data-testid={`item-hotel-room-${idx}`} />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Check-in date</Label>
                <Input type="date" value={item.check_in_date || ""} onChange={(e) => onChange({ ...item, check_in_date: e.target.value })} className={inputCls} data-testid={`item-hotel-checkin-date-${idx}`} />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Check-in time</Label>
                <Input type="time" value={item.check_in_time || ""} onChange={(e) => onChange({ ...item, check_in_time: e.target.value })} className={inputCls} data-testid={`item-hotel-checkin-time-${idx}`} />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Check-out date</Label>
                <Input type="date" value={item.check_out_date || ""} onChange={(e) => onChange({ ...item, check_out_date: e.target.value })} className={inputCls} data-testid={`item-hotel-checkout-date-${idx}`} />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Check-out time</Label>
                <Input type="time" value={item.check_out_time || ""} onChange={(e) => onChange({ ...item, check_out_time: e.target.value })} className={inputCls} data-testid={`item-hotel-checkout-time-${idx}`} />
              </div>
              <div className="sm:col-span-12">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Address / location</Label>
                <Input value={item.address || ""} onChange={(e) => onChange({ ...item, address: e.target.value })} placeholder="Via del Babuino 9, Rome, Italy" className={inputCls} data-testid={`item-hotel-address-${idx}`} />
              </div>
            </>
          )}

          {/* Generic location only for non-flight/hotel types (those use airports / address instead) */}
          {item.type !== "flight" && item.type !== "hotel" && (
            <div className="sm:col-span-6">
              <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Location</Label>
              <Input
                value={item.location || ""}
                onChange={(e) => onChange({ ...item, location: e.target.value })}
                placeholder="Vatican City"
                className={inputCls}
              />
            </div>
          )}

          {/* Shared admin / pricing fields — flights have their own copies inside the Flight Details block above */}
          {!isFlight && (
            <>
              <div className="sm:col-span-3">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Confirmation #</Label>
                <Input
                  value={item.confirmation_number || ""}
                  onChange={(e) => onChange({ ...item, confirmation_number: e.target.value })}
                  placeholder="ABC123"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Supplier / contact</Label>
                <Input
                  value={item.supplier_contact || ""}
                  onChange={(e) => onChange({ ...item, supplier_contact: e.target.value })}
                  placeholder="Maria · +39 06…"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-12">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Description</Label>
                <Textarea
                  value={item.description || ""}
                  onChange={(e) => onChange({ ...item, description: e.target.value })}
                  rows={2}
                  className="bg-black/40 border-white/10 text-white focus:border-[#D4AF37] font-sans"
                />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Price</Label>
                <Input
                  value={item.price || ""}
                  onChange={(e) => onChange({ ...item, price: e.target.value })}
                  placeholder="450"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Cur.</Label>
                <Select value={item.currency || "USD"} onValueChange={(v) => onChange({ ...item, currency: v })}>
                  <SelectTrigger className={inputCls}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ink-surface border-white/10 text-white">
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-7">
                <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1 block font-sans">Link / attachment URL</Label>
                <Input
                  value={item.link || ""}
                  onChange={(e) => onChange({ ...item, link: e.target.value })}
                  placeholder="https://…"
                  className={inputCls}
                />
              </div>
            </>
          )}
        </div>
        <div className="shrink-0 flex flex-col gap-1">
          <Button size="sm" variant="ghost" onClick={onMoveUp} disabled={isFirst} className="text-white/40 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-7 w-7 p-0 disabled:opacity-20">
            <ArrowUp size={13} />
          </Button>
          <Button size="sm" variant="ghost" onClick={onMoveDown} disabled={isLast} className="text-white/40 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-7 w-7 p-0 disabled:opacity-20">
            <ArrowDown size={13} />
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove} className="text-white/40 hover:text-red-400 hover:bg-red-500/10 h-7 w-7 p-0" data-testid={`remove-item-${idx}`}>
            <Trash size={13} weight="duotone" />
          </Button>
        </div>
      </div>
      {isFlight && (
        <FlightLookupDialog
          open={lookupOpen}
          onClose={() => setLookupOpen(false)}
          onPick={(seg) => onChange({
            ...item,
            airline: seg.airline || "",
            flight_number: seg.flight_number || "",
            from_airport: seg.from_airport || "",
            from_city: seg.from_city || "",
            to_airport: seg.to_airport || "",
            to_city: seg.to_city || "",
            departure_date: seg.departure_date || "",
            departure_time: seg.departure_time || "",
            arrival_date: seg.arrival_date || "",
            arrival_time: seg.arrival_time || "",
            time: item.time || seg.departure_time || "",
          })}
          defaultFlightNumber={item.flight_number || ""}
          defaultDate={item.departure_date || ""}
        />
      )}
    </div>
  );
}

function DayCard({ day, dayIdx, totalDays, onUpdate, onRemove, onMoveDay, onPickPhoto }) {
  const updateField = (patch) => onUpdate({ ...day, ...patch });
  const updateItem = (itemIdx, item) => {
    const items = [...(day.items || [])];
    items[itemIdx] = item;
    updateField({ items });
  };
  const addItem = (type = "note") => {
    updateField({
      items: [...(day.items || []), {
        id: newId(), type, title: "", time: "", location: "", description: "",
        confirmation_number: "", supplier_contact: "", price: "", currency: "USD", link: "",
        // Flight-specific defaults (only used when type='flight')
        airline: "", flight_number: "", cabin_class: "",
        from_airport: "", to_airport: "",
        departure_date: "", departure_time: "", arrival_date: "", arrival_time: "",
        // Hotel-specific defaults (only used when type='hotel')
        hotel_name: "", address: "", room_type: "",
        check_in_date: "", check_in_time: "", check_out_date: "", check_out_time: "",
      }],
    });
  };
  const removeItem = (i) => updateField({ items: (day.items || []).filter((_, idx) => idx !== i) });
  const moveItem = (from, to) => {
    if (to < 0 || to >= (day.items || []).length) return;
    updateField({ items: reorder(day.items || [], from, to) });
  };

  return (
    <div className={`border ${day.hidden ? "border-white/10 bg-ink-surface/50" : "border-[#D4AF37]/30 bg-ink-surface"} rounded-lg p-5 sm:p-6 mb-5 transition-colors`} data-testid={`day-card-${dayIdx}`}>
      {day.hidden && (
        <div className="mb-4 -mt-1 flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300 font-sans">
          <EyeSlash size={12} weight="duotone" /> Hidden from client view
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5 pb-4 border-b border-white/10">
        <div className="flex items-center gap-3 flex-1 flex-wrap">
          <div className={`w-12 h-12 rounded-full ${day.hidden ? "bg-white/5 border-white/15" : "bg-[#D4AF37]/15 border-[#D4AF37]/40"} border flex items-center justify-center shrink-0`}>
            <span className={`font-serif text-lg ${day.hidden ? "text-white/40" : "text-[#D4AF37]"}`}>{day.day_number}</span>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Input
              value={day.title || ""}
              onChange={(e) => updateField({ title: e.target.value })}
              placeholder={`Day ${day.day_number} — give it a name`}
              className="bg-transparent border-none text-white font-serif text-2xl px-0 focus-visible:ring-0 placeholder:text-white/30 h-auto py-0"
              data-testid={`day-title-${dayIdx}`}
            />
            <div className="flex items-center gap-2 mt-1">
              <CalendarBlank size={13} className="text-white/40" weight="duotone" />
              <Input
                type="date"
                value={day.date || ""}
                onChange={(e) => updateField({ date: e.target.value })}
                className="bg-transparent border-none text-xs text-white/60 px-0 focus-visible:ring-0 h-7 max-w-[160px]"
                data-testid={`day-date-${dayIdx}`}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => updateField({ hidden: !day.hidden })}
            className={`h-8 w-8 p-0 ${day.hidden ? "text-amber-300 hover:bg-amber-500/10" : "text-white/40 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"}`}
            data-testid={`day-hide-toggle-${dayIdx}`}
            title={day.hidden ? "Show to client" : "Hide from client"}
          >
            {day.hidden ? <EyeSlash size={14} weight="duotone" /> : <Eye size={14} weight="duotone" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onMoveDay(dayIdx, dayIdx - 1)} disabled={dayIdx === 0} className="text-white/40 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 w-8 p-0 disabled:opacity-20">
            <ArrowUp size={14} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onMoveDay(dayIdx, dayIdx + 1)} disabled={dayIdx === totalDays - 1} className="text-white/40 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 w-8 p-0 disabled:opacity-20">
            <ArrowDown size={14} />
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove} className="text-white/40 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" data-testid={`remove-day-${dayIdx}`}>
            <Trash size={14} weight="duotone" />
          </Button>
        </div>
      </div>

      <Textarea
        value={day.summary || ""}
        onChange={(e) => updateField({ summary: e.target.value })}
        placeholder="Optional: a one-line summary for this day…"
        rows={1}
        className="bg-black/30 border-white/10 text-white/80 italic font-sans focus:border-[#D4AF37] mb-3"
        data-testid={`day-summary-${dayIdx}`}
      />

      {/* Cover image */}
      <div className="mb-5">
        <Label className="text-[9px] uppercase tracking-widest text-white/40 mb-1.5 block font-sans flex items-center gap-1.5">
          <ImageIcon size={11} weight="duotone" /> Cover image (optional)
        </Label>
        <div className="flex gap-3 items-stretch">
          <Input
            value={day.cover_image_url || ""}
            onChange={(e) => updateField({ cover_image_url: e.target.value })}
            placeholder="Paste a URL or click 'Suggest photos'…"
            className="bg-black/40 border-white/10 text-white focus:border-[#D4AF37] font-sans h-12 flex-1"
            data-testid={`day-cover-${dayIdx}`}
          />
          <Button
            type="button"
            onClick={() => onPickPhoto(dayIdx, day)}
            className="h-12 bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/25 text-[10px] uppercase tracking-[0.2em] px-4"
            data-testid={`day-suggest-${dayIdx}`}
          >
            <Sparkle size={14} weight="duotone" className="mr-1.5" /> Suggest photos
          </Button>
          {day.cover_image_url && (
            <div className="w-20 h-12 rounded-md overflow-hidden border border-[#D4AF37]/30 shrink-0 bg-black/40">
              <img src={day.cover_image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.2; }} />
            </div>
          )}
        </div>
      </div>

      {/* Render items chronologically by effective time (departure_time, check_in_time, etc.)
          while preserving each item's true index in `day.items` so onChange/Remove/Move callbacks
          still mutate the correct row. Stable sort via array index when times tie. */}
      {(day.items || [])
        .map((item, i) => ({ item, originalIdx: i, t: effectiveItemTime(item) }))
        .sort((a, b) => {
          const c = a.t.localeCompare(b.t);
          return c !== 0 ? c : a.originalIdx - b.originalIdx;
        })
        .map(({ item, originalIdx }, visualIdx, arr) => (
          <ItemRow
            key={item.id || originalIdx}
            item={item}
            idx={`${dayIdx}-${originalIdx}`}
            isFirst={visualIdx === 0}
            isLast={visualIdx === arr.length - 1}
            onChange={(it) => updateItem(originalIdx, it)}
            onRemove={() => removeItem(originalIdx)}
            onMoveUp={() => {
              // Swap with the previous item in visual order
              if (visualIdx === 0) return;
              moveItem(originalIdx, arr[visualIdx - 1].originalIdx);
            }}
            onMoveDown={() => {
              if (visualIdx === arr.length - 1) return;
              moveItem(originalIdx, arr[visualIdx + 1].originalIdx);
            }}
          />
        ))}

      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/10 mt-2">
        <span className="text-[10px] uppercase tracking-widest text-white/40 mr-2 font-sans">Add</span>
        {ITEM_TYPES.slice(0, 7).map((t) => (
          <Button
            key={t.value}
            size="sm"
            variant="ghost"
            onClick={() => addItem(t.value)}
            className="text-white/60 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-3 text-[11px]"
            data-testid={`add-${t.value}-day-${dayIdx}`}
          >
            <t.icon size={13} weight="duotone" className="mr-1" /> {t.label}
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => addItem("note")} className="text-white/60 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-3 text-[11px]">
          <Note size={13} weight="duotone" className="mr-1" /> Note
        </Button>
      </div>
    </div>
  );
}

export function DayByDayTab({ days, onChange, startDate, destination = "" }) {
  const list = days || [];
  const [picker, setPicker] = useState({ open: false, dayIdx: -1, query: "", photos: null, loading: false });

  const openPhotoPicker = (idx, day) => {
    const seed = (day.title || day.summary || destination || "").trim();
    setPicker({ open: true, dayIdx: idx, query: seed, photos: null, loading: false });
    if (seed) runSearch(seed);
  };

  const runSearch = async (q) => {
    setPicker((p) => ({ ...p, loading: true, photos: null }));
    try {
      const { data } = await api.get(`/photos/search?q=${encodeURIComponent(q)}&per_page=8`);
      setPicker((p) => ({ ...p, photos: data.photos || [], loading: false }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo search unavailable");
      setPicker((p) => ({ ...p, loading: false, photos: [] }));
    }
  };

  const selectPhoto = async (photo) => {
    const idx = picker.dayIdx;
    if (idx < 0) return;
    // Update day's cover
    const next = [...list];
    next[idx] = { ...next[idx], cover_image_url: photo.full_url };
    onChange(next);
    setPicker({ open: false, dayIdx: -1, query: "", photos: null, loading: false });
    // Track download per Unsplash ToS (fire-and-forget)
    api.post(`/photos/track-download/${photo.id}`).catch(() => {});
    toast.success(`Photo by ${photo.photographer.name} set`);
  };

  const closePicker = () => setPicker({ open: false, dayIdx: -1, query: "", photos: null, loading: false });

  const updateDay = (idx, patch) => {
    const next = [...list];
    next[idx] = patch;
    onChange(next);
  };
  const addDay = () => {
    const lastDay = list[list.length - 1];
    const nextNumber = list.length + 1;
    let nextDate = "";
    if (lastDay?.date) {
      nextDate = addDaysIso(lastDay.date, 1);
    } else if (startDate) {
      nextDate = addDaysIso(startDate, nextNumber - 1);
    }
    onChange([...list, {
      id: newId(),
      day_number: nextNumber,
      date: nextDate,
      title: "",
      summary: "",
      cover_image_url: "",
      items: [],
    }]);
  };
  const removeDay = (idx) => {
    const next = list.filter((_, i) => i !== idx).map((d, i) => ({ ...d, day_number: i + 1 }));
    onChange(next);
  };
  const moveDay = (from, to) => {
    if (to < 0 || to >= list.length) return;
    const next = reorder(list, from, to).map((d, i) => ({ ...d, day_number: i + 1 }));
    onChange(next);
  };

  if (list.length === 0) {
    return (
      <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-12 text-center" data-testid="day-empty-state">
        <CalendarBlank size={36} className="mx-auto text-[#D4AF37]/60 mb-4" weight="duotone" />
        <p className="font-serif text-2xl text-white mb-2">Build the journey day by day</p>
        <p className="text-white/50 text-sm font-sans max-w-md mx-auto mb-6">
          Start with Day 1, then add flights, hotels, tours, restaurants, and notes for each day.
        </p>
        <Button onClick={addDay} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.25em] h-11 px-6" data-testid="add-first-day-btn">
          <Plus size={16} weight="bold" className="mr-2" /> Add Day 1
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="day-by-day-tab">
      {list.map((day, idx) => (
        <DayCard
          key={day.id || idx}
          day={day}
          dayIdx={idx}
          totalDays={list.length}
          onUpdate={(patch) => updateDay(idx, patch)}
          onRemove={() => removeDay(idx)}
          onMoveDay={moveDay}
          onPickPhoto={openPhotoPicker}
        />
      ))}
      <Button
        onClick={addDay}
        className="w-full bg-transparent border border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 text-[#D4AF37] text-xs uppercase tracking-[0.25em] h-12"
        data-testid="add-day-btn"
      >
        <Plus size={16} weight="bold" className="mr-2" /> Add Day {list.length + 1}
      </Button>

      {/* Photo picker dialog */}
      <Dialog open={picker.open} onOpenChange={(o) => !o && closePicker()}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Choose a <span className="italic text-[#D4AF37]">cover photo</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2" data-testid="photo-picker">
            <form
              onSubmit={(e) => { e.preventDefault(); if (picker.query.trim()) runSearch(picker.query.trim()); }}
              className="flex gap-2"
            >
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
                <Input
                  value={picker.query}
                  onChange={(e) => setPicker((p) => ({ ...p, query: e.target.value }))}
                  placeholder="Search destination, mood, landmark…"
                  className="pl-10 h-11 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] font-sans"
                  autoFocus
                  data-testid="photo-search-input"
                />
              </div>
              <Button type="submit" className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 px-5 text-xs uppercase tracking-widest" data-testid="photo-search-btn">
                Search
              </Button>
            </form>

            {picker.loading ? (
              <div className="py-12 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>
            ) : picker.photos === null ? (
              <p className="text-white/50 text-sm text-center py-8 font-sans">Type a destination above to see photo suggestions.</p>
            ) : picker.photos.length === 0 ? (
              <p className="text-white/50 text-sm text-center py-8 font-sans">No photos found. Try a different search.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-grid">
                {picker.photos.map((photo) => (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => selectPhoto(photo)}
                    className="group relative aspect-[4/3] rounded-md overflow-hidden border border-white/10 hover:border-[#D4AF37] transition-all duration-200 bg-black/40"
                    data-testid={`photo-${photo.id}`}
                  >
                    <img
                      src={photo.thumb_url}
                      alt={photo.alt}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-2 text-left">
                      <p className="text-[9px] text-white/70 truncate font-sans">
                        {photo.photographer.name}
                      </p>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="bg-[#D4AF37] text-black text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-sans">Select</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <p className="text-[10px] text-white/30 italic font-sans pt-2 border-t border-white/5">
              Photos by Unsplash photographers · Free for commercial use
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
